/**
 * Canonical breadcrumb hierarchy for in-app pages (selectedPage values).
 * Each entry: i18n labelKey and parent page id (null = root).
 */

export const PAGE_ROOT = "LandingPage";

/** @type {Record<string, { labelKey: string, parent: string | null }>} */
export const PAGE_BREADCRUMB_CONFIG = {
  LandingPage: { labelKey: "home_page", parent: null },

  // Nav — member area
  ProfilePage: { labelKey: "my_profile", parent: PAGE_ROOT },
  MessagePage: { labelKey: "messages", parent: PAGE_ROOT },
  NewsPage: { labelKey: "news", parent: PAGE_ROOT },
  NewsDetail: { labelKey: "news_page_title", parent: "NewsPage" },
  TrainingProgramPage: { labelKey: "training_program", parent: PAGE_ROOT },
  VideoLibraryPage: { labelKey: "video_library_title", parent: PAGE_ROOT },
  TrainingProgramDetail: { labelKey: "training_program_page_title", parent: "TrainingProgramPage" },
  ProfileBeltsPage: { labelKey: "belt", parent: PAGE_ROOT },
  DigitalCardPage: { labelKey: "membership_card", parent: PAGE_ROOT },
  BoardPage: { labelKey: "board_page_title", parent: PAGE_ROOT },
  DocumentationPage: { labelKey: "documentation_title", parent: PAGE_ROOT },
  PosShopPage: { labelKey: "pos_shop_title", parent: PAGE_ROOT },
  PosShopCheckoutPage: { labelKey: "pos_shop_checkout_title", parent: "PosShopPage" },
  ProfileColorPromotionPage: { labelKey: "color_promotion", parent: PAGE_ROOT },
  SchedulePage: { labelKey: "schedule_title", parent: PAGE_ROOT },
  InstructorSchedulePage: { labelKey: "instructors_agenda", parent: PAGE_ROOT },
  InstructorAvailabilityPage: { labelKey: "instructor_availability_title", parent: PAGE_ROOT },
  MembershipPage: { labelKey: "membership", parent: PAGE_ROOT },
  TimeRegistrationPage: { labelKey: "coach_time_registration", parent: PAGE_ROOT },

  // Profile sub-pages
  AchievementsPage: { labelKey: "achievements", parent: "ProfilePage" },
  MyMentorPage: { labelKey: "my_mentor", parent: "ProfilePage" },
  MySmiliesPage: { labelKey: "my_smilies", parent: "ProfilePage" },
  ProfileManageFamily: { labelKey: "manage_family_members", parent: "ProfilePage" },
  ProfileAddChildPage: { labelKey: "add_family_member", parent: "ProfileManageFamily" },
  ProfileAttendancePage: { labelKey: "attendance", parent: "ProfilePage" },
  MyPaymentsPage: { labelKey: "my_payments_title", parent: "ProfilePage" },
  ParentColorPromotion: { labelKey: "color_promotion", parent: "ProfileManageFamily" },

  // Admin hub
  SettingsPage: { labelKey: "admin_settings", parent: PAGE_ROOT },

  UserAdminPage: { labelKey: "user_admin", parent: "SettingsPage" },
  ApprovalCenterPage: { labelKey: "approval_center", parent: "SettingsPage" },
  AnmalningarPage: { labelKey: "anmalningar_page_title", parent: "SettingsPage" },
  ClubSettingsPage: { labelKey: "club_settings", parent: "SettingsPage" },
  TrainingGroupsPage: { labelKey: "settings_training_groups", parent: "SettingsPage" },
  GroupMailingsPage: { labelKey: "settings_group_mailings", parent: "SettingsPage" },
  FinancePage: { labelKey: "finance_section_economy", parent: "SettingsPage" },
  ExpensesPage: { labelKey: "settings_expenses", parent: "FinancePage" },
  ScheduleHubPage: { labelKey: "settings_schedule_hub_title", parent: "SettingsPage" },
  PosAdminPage: { labelKey: "pos_admin_title", parent: "SettingsPage" },
  PosShopKioskPage: { labelKey: "pos_kiosk_title", parent: "SettingsPage" },
  BeltPromotionPage: { labelKey: "settings_create_promotions", parent: "SettingsPage" },
  PromotionByGroup: { labelKey: "promotion_by_group", parent: "BeltPromotionPage" },
  BeltAdminPage: { labelKey: "settings_belt_records", parent: "SettingsPage" },
  MentorManagerPage: { labelKey: "admin_mentors", parent: "SettingsPage" },
  MentorListPage: { labelKey: "mentor_page", parent: "SettingsPage" },
  ClubAttendanceStats: { labelKey: "attendance_stats", parent: "SettingsPage" },
  LokStodPage: { labelKey: "lok_page_title", parent: "SettingsPage" },
  QRScannerPage: { labelKey: "settings_qr_checkin", parent: "SettingsPage" },
  QRResultPage: { labelKey: "qr_scanner", parent: "QRScannerPage" },
  ContentPage: { labelKey: "settings_news_content", parent: "SettingsPage" },
  TrainingProgramContentPage: { labelKey: "training_program", parent: "SettingsPage" },
  TimerPage: { labelKey: "settings_training_timer", parent: "SettingsPage" },

  PendingPromotionPage: { labelKey: "pending_belt_promotions", parent: "ApprovalCenterPage" },
  PendingColorPromotionPage: { labelKey: "pending_color_promotions", parent: "ApprovalCenterPage" },
  MembersGroupAddUsersPage: { labelKey: "add_users", parent: "TrainingGroupsPage" },
};

/**
 * Walk parent pointers from pageId up to root.
 * @returns {string[]} root → … → pageId
 */
export function getStaticBreadcrumbTrail(pageId) {
  if (!pageId || pageId === PAGE_ROOT) return [PAGE_ROOT];

  const trail = [];
  let current = pageId;
  const seen = new Set();

  while (current && !seen.has(current)) {
    seen.add(current);
    trail.unshift(current);
    const cfg = PAGE_BREADCRUMB_CONFIG[current];
    current = cfg?.parent ?? null;
  }

  if (trail[0] !== PAGE_ROOT) {
    trail.unshift(PAGE_ROOT);
  }

  return trail;
}

export function getPageLabelKey(pageId) {
  return PAGE_BREADCRUMB_CONFIG[pageId]?.labelKey ?? pageId;
}

/**
 * Next breadcrumb trail after navigating to pageId.
 * - Same page / home: normalize trail
 * - Clicking an earlier crumb: truncate
 * - Drilling down (page's parent is current leaf): append
 * - Menu / lateral jump: static hierarchy from config
 *
 * @param {string[]} prevTrail
 * @param {string} pageId
 * @returns {string[]}
 */
export function resolveBreadcrumbTrail(prevTrail, pageId) {
  if (!pageId || pageId === PAGE_ROOT) return [PAGE_ROOT];

  const prev = Array.isArray(prevTrail) && prevTrail.length ? prevTrail : [PAGE_ROOT];
  const idx = prev.indexOf(pageId);
  if (idx >= 0) return prev.slice(0, idx + 1);

  const parent = PAGE_BREADCRUMB_CONFIG[pageId]?.parent ?? PAGE_ROOT;
  const last = prev[prev.length - 1];
  if (parent === last) return [...prev, pageId];

  return getStaticBreadcrumbTrail(pageId);
}
